import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useDeviceType from "../../../custom-hooks/DeviceType";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import {
  plusIcon,
  rightDirectionBlackIcon,
  searchIcon,
} from "../../../assets";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { componentKey, setCloseMasterRoleDialog } from "../SettingsSlice";
import {
  deleteMasterRole,
  getMasterRoleList,
  patchMasterRoleData,
  postMasterRoleData,
} from "../SettingsSaga";
import AddNewRoleItemDialog from "../dialogs/add-role-dailog/AddNewRoleItemDialog";

const RolesScreen = () => {
  const { masterRoleList, closeMasterRoleDialog } = useSelector(
    (state: any) => state[componentKey]
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deviceType = useDeviceType();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [status, setStatus] = useState("");
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);
  const [defaultRow, setDefaultRow] = useState<any>({});
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedMasterRoleId, setSelectedMasterRoleId] = useState<
    string | null
  >(null);

  const columns = [
    {
      field: "name",
      label: <div>Name</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.name}</p>
      ),
    },
    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.description || "-"}
        </p>
      ),
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        const isActive = row?.status === "Active";
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <EventEHRSwitchButton
              checked={isActive}
              onChange={() => {
                dispatch(
                  patchMasterRoleData({
                    masterRoleId: row?.id,
                    payload: { status: isActive ? "InActive" : "Active" },
                  })
                );
              }}
            />
          </div>
        );
      },
    },
    {
      field: "createdAt",
      label: <div>Created At</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">
          {row?.createdAt
            ? moment(row?.createdAt).format("MM/DD/YYYY HH:mm")
            : "-"}
        </p>
      ),
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => (
        <div className="flex items-center">
          <MenuDropdown
            options={[
              {
                label: "Edit",
                onClickCb: () => {
                  setDefaultRow(row);
                  setDialogState("edit");
                },
              },
              {
                label: "Delete",
                onClickCb: () => {
                  setSelectedMasterRoleId(row?.id);
                  setOpenConfirmationDialog(true);
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const rows = masterRoleList?.masterRoles?.map((data: any) => ({
    id: data?.id || "-",
    name: data?.name || "-",
    description: data?.description || "",
    status: data?.status || "-",
    createdAt: data?.createdAt || "",
  }));

  const fetchRoles = () => {
    dispatch(
      getMasterRoleList({
        pageNumber,
        limit: rowPerPage,
        searchValue: searchedValue?.trim(),
        status: status || "",
      })
    );
  };

  useEffect(() => {
    fetchRoles();
  }, [pageNumber, rowPerPage, searchedValue, status]);

  useEffect(() => {
    if (closeMasterRoleDialog) {
      dispatch(setCloseMasterRoleDialog(false));
      setDialogState(null);
      setDefaultRow({});
      setOpenConfirmationDialog(false);
      setSelectedMasterRoleId(null);
      fetchRoles();
    }
  }, [closeMasterRoleDialog]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);
    const value = e.target.value;
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500);
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading title={"Roles"} customStyle={{ color: "#1D1D1D" }} />
          </div>

          <div
            className={`flex ${deviceType === "mobile" || deviceType === "small-tablet" ? "w-full justify-end" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SelectDropdown
                label=""
                name="status"
                placeholderValue="All Status"
                displayEmpty={true}
                value={status}
                options={[
                  { label: "Active", value: "Active" },
                  { label: "In Active", value: "InActive" },
                ]}
                onChangeCb={(e) => {
                  setStatus(e.target.value);
                  setPageNumber(1);
                }}
              />
            </div>
            <div>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e: any) => handleSearchChange(e)}
                size="small"
                label="Search by name"
                fullWidth={deviceType === "mobile" ? "160px" : "300px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"add"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Role"
              variant="contained"
              onClickCb={() => {
                setDefaultRow({});
                setDialogState("add");
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            />
          </div>
        </div>

        <div style={{ overflow: "auto" }}>
          <Table
            columns={columns}
            rows={rows}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: masterRoleList?.totalPages,
              totalCount: masterRoleList?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {dialogState && (
        <AddNewRoleItemDialog
          isOpen={!!dialogState}
          isEdit={dialogState === "edit"}
          defaultMasterRoleData={defaultRow}
          handleClose={() => {
            setDialogState(null);
            setDefaultRow({});
          }}
          handleData={({ roleName, description, status }) => {
            const payload = {
              name: roleName,
              description,
              status,
            };
            if (dialogState === "edit" && defaultRow?.id) {
              dispatch(
                patchMasterRoleData({
                  masterRoleId: defaultRow.id,
                  payload,
                })
              );
            } else {
              dispatch(postMasterRoleData(payload));
            }
          }}
        />
      )}

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Role"
          alertDescription="Are you sure you want to delete this Role? Please confirm to proceed."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
              setSelectedMasterRoleId(null);
            } else if (action === "submit" && selectedMasterRoleId) {
              dispatch(
                deleteMasterRole({ masterRoleId: selectedMasterRoleId })
              );
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default RolesScreen;
